import { useQuery } from "@tanstack/react-query";

import {
  GraphQLAPI,
  GraphQLResult,
  queries,
  GraphQLQuery,
} from "@examspark/api";
import { collectAllPages } from "@examspark/common";
import { IDSK, WorkspaceShare } from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";

// use this method any time you are querying for a resource via direct
// id/sk reference (not a search) to ensure it is licensed properly
export const useQueryCheckSharability = (data: IDSK[]) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["sharability", data],
    queryFn: async () => {
      if (data.length === 0) {
        return [];
      }

      const pages = await collectAllPages(async (nextToken) => {
        const result = await GraphQLAPI.query<
          GraphQLQuery<{
            search: { items: IDSK[]; nextToken: string | undefined };
          }>
        >({
          query: queries.search.search({
            label: "CheckSharability",
            fields: `id sk`,
          }),
          variables: {
            authorizationContext,
            aggsNew: [],
            nextToken,
            limit: 500,
            filter: {
              or: data.map((d) => ({
                id: {
                  eq: d.id,
                },
                sk: {
                  eq: d.sk,
                },
              })),
            },
          },
        });

        return result.data?.search;
      });

      return pages.flatMap((p) => p.items ?? []);
    },
  });
};

export const useQueryListContentLicenses = (
  userId: string | null | undefined,
) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["content-licenses", userId],
    enabled: !!userId,
    queryFn: async () => {
      return collectAllPages(async (nextToken) => {
        const result = await GraphQLAPI.query({
          query: queries.app.listContentLicensesForUser,
          variables: {
            input: {
              id: userId,
              sk: {
                beginsWith: "content-license#",
              },
              workspaceId: authorizationContext.workspaceId,
            },
            nextToken,
            authorizationContext,
          },
        });

        return result.data?.listContentLicenses;
      });
    },
  });
};

export const useQueryListContentSharedToWorkspace = () => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["shared-workspaces"],
    queryFn: async () => {
      const result = (await GraphQLAPI.query({
        query: queries.workspace.listContentSharedToWorkspace,
        variables: {
          authorizationContext,
        },
      })) as GraphQLResult<{
        listContentSharedToWorkspace: WorkspaceShare[];
      }>;

      return result.data?.listContentSharedToWorkspace;
    },
  });
};
